// Import Dependencies
import { useContext, useEffect, Fragment } from "react";
import { createPortal } from "react-dom";
import {
  ChevronUpDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { TbUpload } from "react-icons/tb";
import clsx from "clsx";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { EllipsisHorizontalIcon } from "@heroicons/react/20/solid";
import { SalesPageLayoutContext } from "app/layouts/SalesLayout/SalesPageLayout";

// Local Imports
import {
  Badge,
  Button,
  Input,
} from "components/ui";
import { TableConfig } from "./TableConfig";
import { useBreakpointsContext } from "app/contexts/breakpoint/context";
import { createScopedKeydownHandler } from "utils/dom/createScopedKeydownHandler";
import { canExportLeads } from "permissions/permissionHelpers";

// ----------------------------------------------------------------------

const filterOptions = [
  { label: "In Progress", value: "in-progress" },
  { label: "Pending", value: "pending" },
  { label: "Done", value: "done" },
];

export function Toolbar({
  table,
  title = "",
  subtitle = "",
  titleIcon = null,
  showSearch = true,
  showStatusFilter = true,
  showActionButtons = true,
  showColumnConfig = true,
  onPrint,
  onExportPDF,
  onExportCSV,
  headerActions,
  searchPlaceholder,
  disablePortal = false,
}) {
  const { isXs } = useBreakpointsContext();
  const isFullScreenEnabled = table.getState().tableSettings?.enableFullScreen || table.options.meta?.tableSettings?.enableFullScreen;

  const layoutContext = useContext(SalesPageLayoutContext);

  if (layoutContext?.rightContainer && !disablePortal) {
    // On mobile (isXs) portal into the dedicated mobile row if available; otherwise fallback to layoutContext.rightContainer
    const mobilePortalTarget = typeof document !== 'undefined'
      ? document.getElementById('mobile-right-portal')
      : null;
    const mobileSearchPortalTarget = typeof document !== 'undefined'
      ? document.getElementById('mobile-search-portal')
      : null;
    const desktopPortalTarget = layoutContext.rightContainer;
    const portalTarget = (isXs && mobilePortalTarget) ? mobilePortalTarget : desktopPortalTarget;

    const toolbarContent = (
      <div className={clsx(
        "flex items-center gap-1.5 sm:gap-2 min-w-0 sm:justify-end",
        isXs ? "flex-1 min-w-0" : "flex-wrap"
      )}>
        {showSearch && (
          <div className="relative flex-1 min-w-[120px] sm:w-64 md:w-80 sm:flex-initial shrink-0">
            <SearchInput table={table} searchPlaceholder={searchPlaceholder} />
          </div>
        )}
        <Filters table={table} />
        {showColumnConfig && <TableConfig table={table} />}
        {canExportLeads() && (onExportCSV && title !== "Lead Activities" && (
          <Button
            variant="outlined"
            onClick={onExportCSV}
            className={clsx(
              "h-8 gap-2 whitespace-nowrap text-xs shrink-0 rounded-md flex items-center justify-center border border-gray-300 dark:border-dark-500",
              isXs ? "w-8 p-0" : "w-8 sm:w-auto p-0 sm:px-2.5"
            )}
          >
            <TbUpload className="size-4" />
            {!isXs && (
              <span className="hidden sm:inline">Export</span>
            )}
          </Button>
        ))}
        {/* headerActions always shown — includes Add Vehicle/Leads + Filter on all screen sizes */}
        {headerActions}
      </div>
    );

    return (
      <>
        {portalTarget && createPortal(toolbarContent, portalTarget)}
        {showStatusFilter && !isFullScreenEnabled && table.getColumn("status") ? <StatusFilter table={table} /> : null}
      </>
    );
  }

  return (
    <div className="table-toolbar">


      {showStatusFilter && !isFullScreenEnabled && table.getColumn("status") && <StatusFilter table={table} />}

      {isXs ? (
        <>
          <div
            className={clsx(
              "flex items-center space-x-1.5 pt-4 overflow-x-auto no-scrollbar [&_.input-root]:flex-1 [&_.input-root]:min-w-[100px]",
              isFullScreenEnabled ? "px-4 sm:px-5" : "px-0"
            )}
          >
            {showSearch && <SearchInput table={table} searchPlaceholder={searchPlaceholder} autoFocus={!isXs} />}
            {showColumnConfig && <TableConfig table={table} />}
            {onExportCSV && (
              <Button
                variant="outlined"
                onClick={onExportCSV}
                className={clsx(
                  "h-8 gap-2 whitespace-nowrap text-xs shrink-0 rounded-md flex items-center justify-center border border-gray-300 dark:border-dark-500",
                  "w-8 p-0"
                )}
              >
                <TbUpload className="size-4" />
              </Button>
            )}
            {headerActions}
          </div>
          <div
            className={clsx(
              "hide-scrollbar flex shrink-0 space-x-2 overflow-x-auto pt-3 pb-1",
              isFullScreenEnabled ? "px-4 sm:px-5" : "px-0"
            )}
          >
            <Filters table={table} />
          </div>
        </>
      ) : (
        <div
          className={clsx(
            "transition-content flex items-center justify-between space-x-4 pb-1",
            isFullScreenEnabled ? "px-4 sm:px-5" : "px-0"
          )}
          style={{
            "--margin-scroll": isFullScreenEnabled ? "1.25rem" : "var(--margin-x)",
          }}
        >
          <div className="flex items-center shrink-0 space-x-3">
            <Filters table={table} />
          </div>

          <div className="flex items-center space-x-2">
            {showSearch && (
              <div className="relative flex-1 min-w-0 sm:w-64 md:w-80 sm:flex-initial">
                <SearchInput table={table} searchPlaceholder={searchPlaceholder} />
              </div>
            )}
            {showColumnConfig && <TableConfig table={table} />}
            {onExportCSV && (
              <Button
                variant="outlined"
                onClick={onExportCSV}
                className={clsx(
                  "h-8 gap-2 whitespace-nowrap text-xs shrink-0 rounded-md flex items-center justify-center border border-gray-300 dark:border-dark-500",
                  isXs ? "w-8 p-0" : "w-8 sm:w-auto p-0 sm:px-2.5"
                )}
              >
                <TbUpload className="size-4" />
                {!isXs && (
                  <span className="hidden sm:inline">Export</span>
                )}
              </Button>
            )}
            {headerActions}
          </div>
        </div>
      )}
    </div>
  );
}

function SearchInput({ table, searchPlaceholder, autoFocus = false }) {
  const globalFilter = table.getState().globalFilter || "";
  const showClear = globalFilter.length > 0;

  return (
    <Input
      value={globalFilter}
      onChange={(e) => table.setGlobalFilter(e.target.value)}
      prefix={<MagnifyingGlassIcon className="size-4" />}
      suffix={
        showClear ? (
          <button
            onClick={() => table.setGlobalFilter("")}
            className="flex items-center justify-center p-0.5 rounded-full hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-dark-600 dark:hover:text-dark-100 transition-colors"
            title="Clear search"
          >
            <XMarkIcon className="size-3.5 stroke-[2.5px]" />
          </button>
        ) : null
      }
      classNames={{
        input: "h-8 text-xs truncate",
        root: "w-full",
      }}
      placeholder={searchPlaceholder || "Search..."}
    />
  );
}

function Filters({ table }) {
  const filters = table.getState().columnFilters;

  const isFiltered =
    filters.length > 0 && !(filters.length === 1 && filters[0].id === "status");

  return (
    <>

      {/* {table.getColumn("started_at") && (
        <DateFilter
          column={table.getColumn("started_at")}
          title="Start Date"
          config={{ mode: "range" }}
        />
      )}

      {table.getColumn("deadline") && (
        <DateFilter
          column={table.getColumn("deadline")}
          title="Deadline"
          config={{ mode: "range" }}
        />
      )} */}

      {isFiltered && (
        <Button
          onClick={() => table.resetColumnFilters()}
          className="h-8 px-2.5 text-xs whitespace-nowrap"
        >
          Reset Filters
        </Button>
      )}
    </>
  );
}

function StatusFilter({ table }) {
  const col = table.getColumn("status");
  const val = col?.getFilterValue();
  const isFullScreenEnabled = table.getState().tableSettings?.enableFullScreen || table.options.meta?.tableSettings?.enableFullScreen;

  const handleFilterChange = (value) => col?.setFilterValue(value);
  const facets = col?.getFacetedUniqueValues();

  return (
    <div
      className={clsx(
        "transition-content hide-scrollbar mt-4 mb-1 overflow-x-auto",
        isFullScreenEnabled ? "px-4 sm:px-5" : "px-(--margin-x)"
      )}
    >
      <div className="border-gray-150 dark:border-dark-500 w-max min-w-full border-b-2">
        <div className="-mb-0.5 flex gap-2" data-tab>
          <Button
            onClick={() => handleFilterChange(undefined)}
            className={clsx(
              "relative flex gap-2 border-b-2 px-3 pb-3 font-medium",
              val
                ? "border-transparent"
                : "border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400"
            )}
            unstyled
            data-tab-item
            onKeyDown={createScopedKeydownHandler({
              siblingSelector: "[data-tab-item]",
              parentSelector: "[data-tab]",
              activateOnFocus: true,
              loop: false,
              orientation: "horizontal",
            })}
          >
            <span>All</span>
            <Badge color={val ? "neutral" : "primary"} className="h-4.5 px-1.5">
              {table.getCoreRowModel().rows.length}
            </Badge>
          </Button>
          {filterOptions.map((option) => (
            <Button
              key={option.value}
              onClick={() => handleFilterChange(option.value)}
              className={clsx(
                "relative flex gap-2 border-b-2 px-3 pb-3 font-medium",
                val === option.value
                  ? "border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400"
                  : "border-transparent"
              )}
              unstyled
              data-tab-item
              onKeyDown={createScopedKeydownHandler({
                siblingSelector: "[data-tab-item]",
                parentSelector: "[data-tab]",
                activateOnFocus: true,
                loop: false,
                orientation: "horizontal",
              })}
            >
              <span>{option.label}</span>
              <Badge
                color={val === option.value ? "primary" : "neutral"}
                className="h-4.5 px-1.5"
              >
                {facets?.get(option.value)}
              </Badge>
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}
